<template>
  <modal
    v-if="active"
    width="556px"
    :title="dialogTitle"
    @close="close"
    bodyOverflow="auto"
  >
    <div class="form-container">
      <manage-addon-estimate
        v-if="estimate"
        :data="data"
        :estimate="estimate"
        :finalAmount="isOnOff ? null : amount === 'custom' ? customAmount : amount"
        :pricePerMonth="pricePerMonth"
      />
      <template v-if="!estimate">
        <div v-if="isSwitchOffMode">
          <div class="switch-off-title mb-16">
            {{ planLimitsKeyText[data.planLimit] }}.
            {{ t('The option will be disabled.') }}
          </div>
          <div class="font-weight-bold mb-8 subtitle">
            {{ t('All the settings related to this option will stop working.') }}
          </div>
          <div>
            {{ t('We will return the prorated amount to your account balance.') }}
          </div>
          <div>
            {{
              t(
                'Prorated balance is calculated by the number of days from the moment the option is disabled until the end of the billing period.',
              )
            }}
          </div>
        </div>
        <manage-addon-not-amount v-if="isBuyNotAmountMode" :data="data" />
        <div v-if="isBuyAmountMode">
          <template
            v-if="
              billingAddon.pricing_model === 'stairstep' ||
              billingAddon.pricing_model === 'tiered' ||
              billingAddon.pricing_model === 'volume' ||
              billingAddon.pricing_model === 'per_unit'
            "
          >
            <div
              class="mb-16 amount-title"
              v-html="t('Specify the total number of addons to be owned')"
            ></div>
            <div class="mb-16">{{ t('Addons Owned Now:') }} {{ addonsOwnedNow }}</div>
            <ui-radio-group class="mt-16" v-model="amount">
              <ui-radio v-for="pack in packageList" :value="pack">
                <template #label>
                  <template v-if="data.planLimit === 'dataRetention'">
                    {{
                      tc('${0}/mo per {1} months', pack, [formattedPerMonth(pack), pack])
                    }}
                  </template>
                  <template v-else>
                    {{
                      tc('${0}/mo per {1} units (${2} per unit)', pack, [
                        formattedPerMonth(pack),
                        pack,
                        formattedPerUnit(pack),
                      ])
                    }}
                  </template>
                </template>
              </ui-radio>
              <ui-radio v-if="packageList" value="custom" :label="t('____x.custom')" />
              <div class="green-box mb-16" v-if="amount === 'custom' || !packageList">
                <div class="d-flex justify-space-between align-center">
                  <div class="d-flex align-center custom-amount">
                    <div class="custom-amount__field">
                      {{ planLimitsKeyText[data.planLimit] }}
                      <ui-input
                        v-model="customAmount"
                        class="customAmount"
                        :hideDetails="1"
                        @blur="onCustomBlur"
                      />
                    </div>
                  </div>
                  <div class="pl-16 pr-16" v-if="data.planLimit !== 'dataRetention'">
                    <div v-if="calculatePerUnit()">
                      <span class="green-box__price">
                        ${{ numeral(calculatePerUnit() / 100).format('0,0.00') }}
                      </span>
                    </div>
                    <div class="green-box__cost-per-month" v-if="calculatePerUnit()">
                      {{ t('Cost per Unit') }}
                    </div>
                  </div>
                  <div>
                    <div>
                      <span class="green-box__price">${{ numeral(calculatePerMonth() / 100).format('0,0.00') }}&nbsp;</span><span class="green-box__mo">/ {{ t('mo') }}</span>
                    </div>
                    <div class="green-box__cost-per-month">{{ t('Cost per Month') }}</div>
                  </div>
                </div>
              </div>
            </ui-radio-group>
            <div class="mb-16" v-if="store.billing.maxQuantity[data.planLimit]">
              {{ t('Maximum available:') }}
              {{
                store.billing.maxQuantity[data.planLimit] -
                planFullData.limits[data.planLimit]
              }}
            </div>
            <ui-info
              class="mb-8 mt-16"
              v-if="isAllowedToBuyOnlyHundreds && amount === 'custom'"
            >
              <p
                v-html="
                  t(
                    'You can purchase min 100 elements.<br>The amount entered in the Custom filed will be rounded to the closest value multiple of 100.<br>e.g<br>13 -> 100<br>101 -> 200<br>1234 -> 1300',
                  )
                "
              ></p>
            </ui-info>
            <ui-info
              class="mb-8 mt-16"
              v-if="isAllowedToBuyOnlyDozens && amount === 'custom'"
            >
              <p
                v-html="
                  t(
                    'You can purchase min 10 elements.<br>The amount entered in the Custom filed will be rounded to the closest value multiple of 10.<br>e.g<br>8 -> 10<br>22 -> 30',
                  )
                "
              ></p>
            </ui-info>
            <p>
              {{
                t(
                  'The amount will be calculated from the current day till the end of the billing period.',
                )
              }}
            </p>
            <p
              v-html="
                t(
                  'Next payment should be performed on the subscription renewal on <b>{0}</b>.',
                  [formatBillingDate(nextBillingAt)],
                )
              "
            ></p>
            <p>
              {{
                t(
                  'To cancel the add-on, access the Subscription section and click on Manage button.',
                )
              }}
            </p>
            <p>
              {{ t('If any assistance is required - contact the team via Live Chat.') }}
            </p>
          </template>
        </div>
      </template>
    </div>
    <template #footer>
      <section class="justify-space-between d-flex align-items-center">
        <div>
          <error-aggregator v-model="errors" v-model:active="errorsControlActive" />
        </div>
        <div class="d-flex justify-end">
          <ui-button
            @click="switchOff"
            button-style="outline-alt"
            icon="fas fa-times"
            v-if="isManagingMode && !estimate"
            :text="t('Switch Off Add-On')"
            :disabled="submitButtonLoading"
          />
          <ui-button
            v-if="estimate"
            :text="t('Back')"
            icon="fas fa-arrow-left"
            @click="backFromEstimate"
          />
          <ui-button
            v-if="!estimate"
            icon="fas fa-times"
            :text="t('Cancel')"
            @click="close"
            :disabled="submitButtonLoading || switchOffButtonLoading"
          />
          <ui-button
            @click="submit"
            button-style="accent"
            icon="fas fa-check"
            :disabled="isSubmitDisabled"
            :text="submitButtonText"
            :loading="submitButtonLoading"
          />
        </div>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  deleteBillingAddonApiRequest,
  getBillingSubscriptionApiRequest,
  putBillingAddonApiRequest,
} from '@/services/api/billing-api';
import ManageAddonEstimate from '@/components/dialogs/manage-addon/manage-addon-estimate.vue';
import ManageAddonNotAmount from '@/components/dialogs/manage-addon/manage-addon-not-amount.vue';
import numeral from 'numeral';
import { useI18n, useIntercom, useMixpanel, usePlanLimits } from '@/composables';
import { formatBillingDate, getNextBillingAt } from '@/services/utils/billing-format';
import { useStore } from '@/store';
import { useApiFieldErrors } from '@/composables/useApiFieldErrors';
import { $emitter } from '@/services/emitter';

type BillingAddonTier = {
  starting_unit: number;
  ending_unit?: number | null;
  price: number;
};
type BillingAddon = {
  pricing_model: 'flat_fee' | 'tiered' | 'volume' | 'stairstep' | 'per_unit';
  price: number;
  tiers: BillingAddonTier[];
};
type AddonPackage = number[] | undefined;

const props = defineProps<{
  data: {
    planLimit: keyof BillingLimits;
  };
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t, tc } = useI18n();
const { openLiveSupport } = useIntercom();
const { planLimitsKeyText } = usePlanLimits(undefined);
const { trackMixpanel } = useMixpanel();
const store = useStore();
const nextBillingAt = computed(() => getNextBillingAt(store.billing));

const active = ref(false);
const switchOffButtonLoading = ref(false);
const submitButtonLoading = ref(false);
const estimate = ref<any>(null);
const customAmount = ref<number>(0);
const amount = ref<number | 'custom'>(0);
const { errors, errorsControlActive, clearErrors, applyApiErrors } = useApiFieldErrors();

onMounted(() => {
  active.value = true;
  const addonLimit = store.billing.addons?.[props.data.planLimit]?.addonLimit || 0;
  customAmount.value = addonLimit;
  amount.value = addonLimit;
});

const isAddonEnabled = computed(() => !!store.billing.addons?.[props.data.planLimit]);

const addonsOwnedNow = computed(
  () => store.billing.addons?.[props.data.planLimit]?.addonLimit || 0,
);

const dialogTitle = computed(() => {
  if (isSwitchOffMode.value || estimate.value?.estimateType === 'delete')
    return t('Switch Off Add-on');
  if (estimate.value) return t('Confirm Purchase');
  if (!isAddonEnabled.value) return t('Buy as Add-on');
  if (isManagingMode.value) return t('Manage Add-ons');
  return '';
});

const isAllowedToBuyOnlyHundreds = computed(() =>
  ['campaignsAmount', 'offersAmount', 'landingsAmount', 'flowsAmount'].includes(props.data.planLimit),
);

const isAllowedToBuyOnlyDozens = computed(() =>
  ['trafficSourcesAmount', 'affiliateNetworksAmount'].includes(props.data.planLimit),
);

const billingAddon = computed(
  () => store.billingAddons[props.data.planLimit] as BillingAddon,
);

const isOnOff = computed(() => billingAddon.value.pricing_model === 'flat_fee');

const calculatePerMonth = (value = customAmount.value) => {
  const addon = billingAddon.value;

  const currentAmount = Number(value);
  if (Number.isNaN(currentAmount)) return;
  if (currentAmount === 0) return 0;

  if (addon.pricing_model === 'tiered') {
    let restCustomValue = currentAmount;
    return addon.tiers.reduce((acc, tier) => {
      if (currentAmount >= tier.starting_unit && !tier.ending_unit) {
        acc = acc + restCustomValue * tier.price;
      }
      if (tier.ending_unit !== null && currentAmount > tier.ending_unit) {
        const unitAmount = tier.ending_unit - tier.starting_unit + 1;
        restCustomValue = restCustomValue - unitAmount;
        acc = acc + unitAmount * tier.price;
      }
      if (
        tier.ending_unit !== null &&
        currentAmount >= tier.starting_unit &&
        currentAmount <= tier.ending_unit
      ) {
        const unitAmount = currentAmount - tier.starting_unit + 1;
        restCustomValue = restCustomValue - unitAmount;
        acc = acc + unitAmount * tier.price;
      }
      return acc;
    }, 0);
  }

  if (addon.pricing_model === 'volume') {
    const tier = addon.tiers.find(tierItem => {
      if (
        currentAmount >= tierItem.starting_unit &&
        currentAmount <= tierItem.ending_unit
      ) {
        return true;
      }
      return currentAmount >= tierItem.starting_unit && !tierItem.ending_unit;
    });
    return (tier?.price || 0) * currentAmount;
  }

  if (addon.pricing_model === 'stairstep') {
    const tier = addon.tiers.find(tierItem => {
      if (
        currentAmount >= tierItem.starting_unit &&
        currentAmount <= tierItem.ending_unit
      ) {
        return true;
      }
      return currentAmount >= tierItem.starting_unit && !tierItem.ending_unit;
    });
    return tier?.price || 0;
  }

  if (addon.pricing_model === 'per_unit') {
    return addon.price * currentAmount;
  }
};

const calculatePerUnit = () =>
  Math.round((calculatePerMonth() || 0) / customAmount.value);

const formattedPerMonth = (pack: number) =>
  numeral(Math.round(calculatePerMonth(pack) || 0) / 100).format('0,0.00');

const formattedPerUnit = (pack: number) =>
  numeral(Math.round((calculatePerMonth(pack) || 0) / pack) / 100).format(
    '0,0.00',
  );

const onCustomBlur = () => {
  if (isAllowedToBuyOnlyHundreds.value) {
    customAmount.value = Math.ceil(customAmount.value / 100) * 100;
    return;
  }
  if (isAllowedToBuyOnlyDozens.value) {
    customAmount.value = Math.ceil(customAmount.value / 10) * 10;
    return;
  }
};

const submitButtonText = computed(() => {
  if (isSwitchOffMode.value) return t('Switch Off');
  if (props.data.planLimit === 'uniqOfferViewerCondition') return t('Contact Team');
  if (estimate.value) return t('Confirm');
  if (!isAddonEnabled.value) return t('Buy Now');
  if (isManagingMode.value) return t('Update');
  return '';
});

const isSubmitDisabled = computed(
  () =>
    switchOffButtonLoading.value || estimate.value?.estimateType === 'lessThan50cents',
);

const isManagingMode = computed(() => !isOnOff.value && isAddonEnabled.value);
const isSwitchOffMode = computed(() => isOnOff.value && isAddonEnabled.value);
const isBuyAmountMode = computed(() => !isOnOff.value);
const isBuyNotAmountMode = computed(() => isOnOff.value && !isAddonEnabled.value);

const planFullData = computed(
  () => store.billingPlans.find(plan => plan.id === store.billing.planId)!,
);

const packageList = computed<AddonPackage>(() => {
  switch (props.data.planLimit) {
    case 'campaignsAmount':
    case 'offersAmount':
    case 'landingsAmount':
    case 'flowsAmount':
    case 'trafficSourcesAmount':
    case 'affiliateNetworksAmount':
      return [100, 500, 1000];
    case 'customColumnsAmount':
      return [1, 5, 10, 20];
    case 'memberAdminsAmount':
    case 'memberUsersAmount':
    case 'customConversionsAmount':
      return [1, 3, 5, 10];
    case 'domainsAmount':
    case 'sharedReportsAmount':
    case 'redirectDomainsAmount':
      return [1, 3, 10];
    case 'dataRetention':
      return [3, 6, 9, 12];
    default:
      return undefined;
  }
});

const amountToSend = computed(() => {
  if (isOnOff.value) return null;
  if (amount.value === 'custom' || !packageList.value) return customAmount.value;
  return amount.value;
});

const pricePerMonth = computed(() => {
  if (isOnOff.value) return billingAddon.value.price;
  const value = calculatePerMonth(
    amount.value === 'custom' ? customAmount.value : amount.value,
  );
  return value || 0;
});

const stopLoading = () => {
  switchOffButtonLoading.value = false;
  submitButtonLoading.value = false;
};

const submit = async () => {
  if (props.data.planLimit === 'uniqOfferViewerCondition') {
    openLiveSupport();
    return;
  }
  submitButtonLoading.value = true;
  const managingMode = isManagingMode.value;
  if (!estimate.value) {
    await estimateAddon();
    return;
  }
  if (isSwitchOffMode.value || estimate.value.estimateType === 'delete') {
    await deleteAddon();
    return;
  }
  try {
    await putBillingAddonApiRequest({
      id: props.data.planLimit,
      quantity: amountToSend.value,
    });
    store.setBilling(await getBillingSubscriptionApiRequest());
    afterSubmit({
      message: managingMode ? t('Updated successfully') : t('Added successfully'),
    });
    trackMixpanel(managingMode ? 'addon_edit' : 'addon_add', {
      name: props.data.planLimit,
      planId: store.billing.planId,
      quantity: amountToSend.value,
    });
  } catch (error) {
    applyApiErrors(error, { nested: true });
    throw error;
  } finally {
    stopLoading();
  }
};

const afterSubmit = ({ message }: { message: string }) => {
  $emitter.emit('toast-success', message);
  close();
};

const estimateAddon = async () => {
  clearErrors();
  if (isSwitchOffMode.value) {
    return deleteAddonEstimate();
  }
  try {
    const payload = await putBillingAddonApiRequest({
      id: props.data.planLimit,
      type: 'estimates',
      quantity: amountToSend.value as number,
    });
    estimate.value = payload;
  } catch (error: any) {
    if (
      error?.response?.data?.message === 'SUBSCRIPTION_UPDATE_TOTAL_LESS_THAN_50_CENTS'
    ) {
      estimate.value = {
        ...error.response.data.payload,
        estimateType: 'lessThan50cents',
      };
      return;
    }
    applyApiErrors(error, { nested: true });
    throw error;
  } finally {
    stopLoading();
  }
};

const switchOff = () => {
  switchOffButtonLoading.value = true;
  deleteAddonEstimate();
};

const deleteAddonEstimate = async () => {
  clearErrors();
  try {
    const payload = await deleteBillingAddonApiRequest({
      id: props.data.planLimit,
      type: 'estimates',
    });
    estimate.value = {
      ...(payload as Record<string, unknown>),
      estimateType: 'delete',
    };
  } catch (error) {
    applyApiErrors(error, { nested: true });
    throw error;
  } finally {
    stopLoading();
  }
};

const deleteAddon = async () => {
  try {
    await deleteBillingAddonApiRequest({
      id: props.data.planLimit,
    });
    store.setBilling(await getBillingSubscriptionApiRequest());
    $emitter.emit('toast-success', t('Switched off successfully'));
    close();
  } catch (error) {
    applyApiErrors(error, { nested: true });
    throw error;
  } finally {
    stopLoading();
    trackMixpanel('addon_delete', {
      name: props.data.planLimit,
      planId: store.billing.planId,
    });
  }
};

const backFromEstimate = () => {
  estimate.value = '';
};

const close = () => {
  emit('close');
};
</script>

<style lang="scss" scoped>
@import '@/styles/components/manage-addon.scss';
.modal-title {
  font-size: 14px;
  font-weight: bold;
}

.subtitle {
  font-size: 14px;
}

.green-box > .d-flex > * {
  flex: 1 1 auto;
}

.green-box > .d-flex > .custom-amount {
  flex: 0 1 auto;
}

.custom-amount__field {
  width: 100%;
}

.customAmount {
  width: 230px;
  max-width: 100%;

  :deep(.v-input),
  :deep(.v-input__control),
  :deep(.v-field),
  :deep(.v-field__field),
  :deep(.v-field__input) {
    width: 100%;
  }
}

.amount-title {
  font-weight: bold;
  font-size: 14px;
}

.switch-off-title {
  font-size: 20px;
  font-weight: bold;
}
</style>
